// Który z dwóch wyników tego samego zadania zostaje, gdy dziecko cofnęło się („←”) i zrobiło je jeszcze raz (patch-49).
// Zadania, które dają GWIAZDKI (mówienie: `speak`, `say`): liczy się LEPSZY wynik, nigdy gorszy — powtórka może podnieść gwiazdki, a pomyłka przy poprawianiu niczego nie odbiera.
// Pozostałe zadania (pytanie, luka, układanka) nie dają punktów, tylko mówią, czy dziecko znało słowo przy pierwszym spotkaniu (statystyka słowa i pudełka powtórek),
// więc zostaje PIERWSZY wynik: odpowiedź podana drugi raz, z pamięci, niczego o słowie nie mówi. Remis zostawia pierwszy wynik.
import type { Outcome } from '../types.ts';

/** Klucz porównania: im większy (leksykograficznie), tym lepszy wynik. Pola, których zadanie nie ma, liczą się jak najgorsze. */
function rank(o: Outcome): number[] {
  return [
    o.passed === true ? 1 : 0, // „powiedz zdanie” / „Co powiesz?”: zaliczone w którymkolwiek podejściu
    o.correct === true ? 1 : 0, // pytanie, luka, układanka, „Co powiesz?”: bez pomyłki i bez podpowiedzi
    o.stars ?? 0,
    o.percent ?? -1, // wymowa w % (tylko ocena Azure)
    -(o.attempts ?? Number.MAX_SAFE_INTEGER), // przy remisie — mniej podejść
  ];
}

/** `true`, gdy `next` jest ściśle lepszy niż `prev` (ten sam rodzaj zadania) i to zadanie w ogóle podnosi wynik (mówienie). */
export function isBetterOutcome(next: Outcome, prev: Outcome): boolean {
  if (next.kind !== 'speak' && next.kind !== 'say') return false;
  const a = rank(next);
  const b = rank(prev);
  for (let i = 0; i < a.length; i++) {
    if (a[i]! !== b[i]!) return a[i]! > b[i]!;
  }
  return false;
}

/**
 * Dopisuje wynik zadania do listy wyników lekcji: nowe zadanie — na koniec; zadanie zrobione już raz — lepszy wynik mówienia zastępuje gorszy na tym samym miejscu
 * (kolejność nie zmienia się). `keepFirst` (powtórka dnia: nie ma gwiazdek, a pierwsza odpowiedź to miara słowa) zostawia zawsze pierwszy wynik.
 * Zwraca `true`, gdy lista się zmieniła.
 */
export function recordOutcome(outcomes: Outcome[], next: Outcome, keepFirst = false): boolean {
  const at = outcomes.findIndex((o) => o.taskId === next.taskId);
  if (at < 0) {
    outcomes.push(next);
    return true;
  }
  if (keepFirst || !isBetterOutcome(next, outcomes[at]!)) return false;
  outcomes[at] = next;
  return true;
}
